const finiteNonNegative = (value) => (Number.isFinite(Number(value)) ? Math.max(0, Number(value)) : 0);
const boundedInteger = (value, max = 1000000) => Math.min(max, Math.round(finiteNonNegative(value)));
const ALLOWED_MODALITIES = new Set(['CT', 'MR', 'DX', 'CR', 'MG', 'US', 'NM', 'PT', 'XA', 'RF', 'OT', 'SR']);
const ALLOWED_RESOURCE_PREFIXES = ['/api/search/studies/', '/api/dicom/', '/render/', '/api/render/'];

const normalizeModality = (value) => {
  const modality = String(value || 'OT').trim().toUpperCase();
  return ALLOWED_MODALITIES.has(modality) ? modality : 'OT';
};

export const isStudyOpenResourceName = (value, {
  locationObject = typeof window !== 'undefined' ? window.location : undefined,
  studyIdentity = '',
} = {}) => {
  try {
    const base = locationObject?.origin || 'https://kairos.invalid';
    const url = new URL(String(value || ''), base);
    if (locationObject?.origin && url.origin !== locationObject.origin) return false;
    if (!ALLOWED_RESOURCE_PREFIXES.some(prefix => url.pathname.startsWith(prefix))) return false;
    if (!studyIdentity) return true;
    return url.pathname.split('/').some(segment => {
      try { return decodeURIComponent(segment) === String(studyIdentity); } catch (_) { return false; }
    });
  } catch (_) {
    return false;
  }
};

export const classifyStudyPerformance = (metrics = {}) => {
  const firstImageMs = finiteNonNegative(metrics.first_image_ms);
  if (firstImageMs > 0 && firstImageMs <= 2000) {
    return { code: 'healthy', confidence: 'high', summary: 'Study reached accepted diagnostic pixels within the expected interactive window.' };
  }
  if (!firstImageMs) {
    return { code: 'mixed', confidence: 'low', summary: 'No accepted diagnostic pixel was observed before collection closed.' };
  }
  const kairosMs = finiteNonNegative(metrics.kairos_ms);
  const requestPathMs = finiteNonNegative(metrics.request_path_ms);
  const workstationMs = finiteNonNegative(metrics.long_task_ms);
  if (kairosMs >= 250 && kairosMs >= workstationMs && kairosMs / firstImageMs >= 0.35) {
    return { code: 'kairos', confidence: 'medium', summary: 'Server-owned Kairos timing is a substantial measured contributor.' };
  }
  if (workstationMs >= 250 && workstationMs / firstImageMs >= 0.35) {
    return { code: 'workstation', confidence: 'medium', summary: 'Browser main-thread stalls are a substantial measured contributor.' };
  }
  if (requestPathMs >= 250 && requestPathMs / firstImageMs >= 0.35) {
    return { code: 'request_path', confidence: 'low', summary: 'The browser observed a slow Kairos request path; server versus transport is not separable without Server-Timing.' };
  }
  return { code: 'mixed', confidence: 'low', summary: 'Measured evidence does not isolate a single cause.' };
};

export const getBrowserPerformanceContext = ({ navigatorObject = typeof navigator !== 'undefined' ? navigator : undefined } = {}) => {
  const userAgent = String(navigatorObject?.userAgent || '');
  const browserEngine = /firefox/i.test(userAgent) ? 'firefox'
    : /applewebkit/i.test(userAgent) && !/(chrome|chromium|crios|edg)/i.test(userAgent) ? 'webkit'
      : /(chrome|chromium|crios|edg)/i.test(userAgent) ? 'chromium' : 'unknown';
  const platformClass = /ipad|tablet/i.test(userAgent) ? 'tablet'
    : /android|iphone|mobile/i.test(userAgent) ? 'mobile' : 'desktop';
  const connection = navigatorObject?.connection;
  const effectiveType = String(connection?.effectiveType || '').toLowerCase();
  const connectionClass = navigatorObject?.onLine === false ? 'offline'
    : effectiveType === 'slow-2g' || effectiveType === '2g' ? 'slow'
      : effectiveType === '3g' ? 'typical'
        : effectiveType === '4g' ? 'fast' : 'unknown';
  return { browserEngine, platformClass, connectionClass };
};

export const observeBrowserPerformance = (tracker, {
  PerformanceObserverClass = typeof PerformanceObserver !== 'undefined' ? PerformanceObserver : undefined,
  locationObject = typeof window !== 'undefined' ? window.location : undefined,
  studyIdentity = '',
} = {}) => {
  if (!tracker || typeof PerformanceObserverClass !== 'function') return () => {};
  const observers = [];
  let stopped = false;
  const connect = (type, handleEntries) => {
    try {
      const observer = new PerformanceObserverClass((entryList) => handleEntries(entryList.getEntries?.() || []));
      observer.observe({ type, buffered: false });
      observers.push({ observer, handleEntries });
    } catch (_) {
      // Browser instrumentation must remain clinically fail-open.
    }
  };
  connect('resource', (entries) => entries.forEach((entry) => {
    if (!['fetch', 'xmlhttprequest', 'img'].includes(String(entry.initiatorType || '').toLowerCase())) return;
    if (!isStudyOpenResourceName(entry.name, { locationObject, studyIdentity })) return;
    const kairosMs = (Array.from(entry.serverTiming || []))
      .filter(timing => String(timing?.name || '').toLowerCase() === 'kairos')
      .reduce((max, timing) => Math.max(max, finiteNonNegative(timing.duration)), 0);
    tracker.observeResource({
      requestPathMs: finiteNonNegative(entry.duration),
      kairosMs,
      transferBytes: finiteNonNegative(entry.transferSize),
    });
  }));
  connect('longtask', (entries) => entries.forEach(entry => tracker.observeLongTask(entry.duration)));
  return () => {
    if (stopped) return;
    stopped = true;
    observers.forEach(({ observer, handleEntries }) => {
      try { handleEntries(observer.takeRecords?.() || []); } catch (_) {}
      try { observer.disconnect(); } catch (_) {}
    });
  };
};

export const createStudyPerformanceTracker = ({
  now = () => performance.now(),
  createId = () => (typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
      const random = Math.floor(Math.random() * 16);
      return (character === 'x' ? random : (random & 0x3) | 0x8).toString(16);
    })),
} = {}) => {
  const startedAt = now();
  const clientRunId = createId();
  const milestones = { start: 0 };
  const totals = { request_count: 0, request_path_ms: 0, kairos_ms: 0, long_task_ms: 0, transfer_bytes: 0 };
  const metadata = { series_count: 0, instance_count: 0, viewport_count: 0, painted_viewport_count: 0 };
  const seriesByOrdinal = new Map();
  const acceptedViewportOrdinals = new Set();
  let closed = false;
  let outcome = 'collecting';

  const elapsed = () => boundedInteger(now() - startedAt, 24 * 60 * 60 * 1000);
  const markOnce = (name) => {
    if (closed) return milestones[name];
    if (!Object.prototype.hasOwnProperty.call(milestones, name)) milestones[name] = elapsed();
    return milestones[name];
  };

  return {
    markMetadataReady({ seriesCount = 0, instanceCount = 0 } = {}) {
      if (closed) return milestones.metadata;
      metadata.series_count = boundedInteger(seriesCount, 10000);
      metadata.instance_count = boundedInteger(instanceCount, 1000000);
      return markOnce('metadata');
    },
    markLayoutReady({ viewportCount = 0 } = {}) {
      if (closed) return milestones.layout;
      metadata.viewport_count = boundedInteger(viewportCount, 64);
      return markOnce('layout');
    },
    markAcceptedVisible({ viewportIndex = 0, seriesOrdinal = 0, modality = 'OT', instanceCount = 0 } = {}) {
      if (closed) return milestones.first_image;
      const firstVisibleMs = markOnce('first_image');
      const viewportOrdinal = boundedInteger(viewportIndex, 64);
      acceptedViewportOrdinals.add(viewportOrdinal);
      metadata.painted_viewport_count = Math.min(64, acceptedViewportOrdinals.size);
      const ordinal = boundedInteger(seriesOrdinal, 10000);
      if (!seriesByOrdinal.has(ordinal)) {
        seriesByOrdinal.set(ordinal, {
          ordinal,
          viewport_ordinal: viewportOrdinal,
          modality: normalizeModality(modality),
          instance_count: boundedInteger(instanceCount, 1000000),
          first_visible_ms: firstVisibleMs,
        });
      }
      return firstVisibleMs;
    },
    markInteractive() { return markOnce('interactive'); },
    observeResource({ requestPathMs = 0, kairosMs = 0, transferBytes = 0 } = {}) {
      if (closed) return;
      totals.request_count = Math.min(1000000, totals.request_count + 1);
      totals.request_path_ms = Math.max(totals.request_path_ms, finiteNonNegative(requestPathMs));
      totals.kairos_ms = Math.max(totals.kairos_ms, finiteNonNegative(kairosMs));
      totals.transfer_bytes += finiteNonNegative(transferBytes);
    },
    observeLongTask(durationMs) {
      if (!closed) totals.long_task_ms += finiteNonNegative(durationMs);
    },
    close({ outcome: nextOutcome = 'partial', expectedViewportCount } = {}) {
      if (closed) return false;
      if (Number.isFinite(Number(expectedViewportCount))) metadata.viewport_count = boundedInteger(expectedViewportCount, 64);
      const requestedOutcome = ['complete', 'partial', 'timeout', 'cancelled'].includes(nextOutcome) ? nextOutcome : 'partial';
      const everyExpectedViewportAccepted = metadata.viewport_count > 0
        && metadata.painted_viewport_count >= metadata.viewport_count;
      outcome = requestedOutcome === 'complete' && !everyExpectedViewportAccepted ? 'partial' : requestedOutcome;
      closed = true;
      return true;
    },
    isClosed() { return closed; },
    snapshot({ modality = 'OT', studyRole = 'primary', browserEngine = 'unknown', platformClass = 'desktop', connectionClass = 'unknown' } = {}) {
      const report = {
        schema_version: 1,
        client_run_id: clientRunId,
        modality: normalizeModality(modality),
        study_role: ['primary', 'prior', 'comparison'].includes(studyRole) ? studyRole : 'primary',
        outcome,
        ...metadata,
        metadata_ms: milestones.metadata ?? null,
        layout_ms: milestones.layout ?? null,
        first_image_ms: milestones.first_image ?? null,
        interactive_ms: milestones.interactive ?? null,
        ...Object.fromEntries(Object.entries(totals).map(([key, value]) => [key, boundedInteger(value, key === 'transfer_bytes' ? 1024 * 1024 * 1024 : 24 * 60 * 60 * 1000)])),
        browser_engine: ['chromium', 'firefox', 'webkit'].includes(browserEngine) ? browserEngine : 'unknown',
        platform_class: ['desktop', 'mobile', 'tablet'].includes(platformClass) ? platformClass : 'desktop',
        connection_class: ['offline', 'slow', 'typical', 'fast'].includes(connectionClass) ? connectionClass : 'unknown',
        // Milestones above are independent timestamps from open start, not serial causal phases.
        phases: [],
        series: [...seriesByOrdinal.values()].slice(0, 256),
      };
      return { ...report, diagnosis: classifyStudyPerformance(report) };
    },
  };
};
